/**
 * Схема данных Kairo — единый контракт для всех модулей.
 *
 * Соглашения, обязательные для каждой сущности:
 *
 * 1. **Soft-delete.** Записи не удаляются физически: ставится `deletedAt`.
 *    Это даёт «отменить удаление» и корректное разрешение конфликтов при
 *    синхронизации (удаление на одном устройстве не воскресает с другого).
 *
 * 2. **`revision` + `updatedAt`.** Счётчик версий и метка времени — основа
 *    стратегии last-write-wins при слиянии с сервером.
 *
 * 3. **Локальная дата (`IsoDate`) вместо таймстампа** для всего, что человек
 *    воспринимает как «день»: замеры, отметки привычек, приёмы лекарств.
 *
 * 4. **Минуты от полуночи (`MinutesOfDay`)** для времени внутри дня.
 *
 * При изменении схемы обязательно добавьте миграцию в `database.ts` —
 * у пользователей на устройствах уже лежат данные предыдущих версий.
 */

import type { SphereId } from '@/shared/config/spheres';
import type { IsoDate, MinutesOfDay } from '@/shared/lib/datetime';

/** Состояние синхронизации записи с сервером. */
export type SyncState = 'local' | 'pending' | 'synced';

/** Поля, общие для всех сущностей. */
export interface BaseEntity {
  id: string;
  /** Владелец записи. Позволяет держать в одной БД несколько аккаунтов. */
  userId: string;
  createdAt: number;
  updatedAt: number;
  /** Метка мягкого удаления (epoch ms). `null` — запись активна. */
  deletedAt: number | null;
  revision: number;
  syncState: SyncState;
}

/* ═══════════════════════════ Пользователь ═══════════════════════════ */

export interface User {
  id: string;
  email: string;
  name: string;
  /** Хеш пароля. В демо-режиме — локальный SHA-256; на сервере не хранится. */
  passwordHash: string;
  createdAt: number;
  avatarColor: string;
}

export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Профиль: настройки, влияющие на расчёты.
 * Хранится отдельно от `User`, потому что меняется часто и синхронизируется.
 */
export interface Profile extends BaseEntity {
  name: string;
  /** Рост в см — база для расчёта ИМТ на вкладке «Здоровье». */
  heightCm: number | null;
  birthDate: IsoDate | null;
  gender: 'male' | 'female' | 'unspecified';
  theme: ThemeMode;
  /**
   * Доля свободного времени, которую разрешено занимать задачами (0–1).
   * По умолчанию 0.8: остальные 20% — буфер на непредвиденное.
   */
  planningLoadFactor: number;
  /** Автоматически переносить незакрытые задачи на следующий день. */
  autoRollover: boolean;
  /**
   * Дневная норма шагов. Десять тысяч — прижившийся ориентир, но у человека
   * с травмой или, наоборот, у бегуна цель своя, поэтому она настраивается.
   */
  stepsGoal: number;
  /** Показывать мотивирующую анимацию при закрытии задачи. */
  celebrationsEnabled: boolean;
  notificationsEnabled: boolean;
  onboardingCompletedAt: number | null;
}

/* ═══════════════════════════ Сферы жизни ═══════════════════════════ */

/**
 * Своя сфера жизни — в дополнение к восьми встроенным.
 *
 * Встроенные сферы в базе не лежат: они одинаковы у всех и нужны раньше, чем
 * открывается база. Здесь только созданные человеком — «Учёба сына», «Дача»,
 * «Волонтёрство»: жизнь шире восьми ячеек, и загонять её в чужую разметку
 * означало бы получить свалку в «Прочем».
 */
export interface CustomSphere extends BaseEntity {
  title: string;
  /** Короткая подпись для чипов и диаграммы, где мало места. */
  short: string;
  /** Что относить к сфере — подсказка под выбором. */
  hint: string;
  emoji: string;
  /**
   * Ключ цвета из палитры приложения — `teal`, `plum`, …
   *
   * Именно ключ, а не готовый `oklch(...)`: цвета заданы переменными темы и в
   * тёмной берутся светлее, а записанное однажды значение так бы и осталось
   * тёмным на тёмном фоне.
   */
  color: string;
  /** Порядок после встроенных сфер. */
  position: number;
}

/* ═══════════════ Режим дня: сон и питание (зелёные слоты) ═══════════════ */

export type RoutineKind = 'sleep' | 'meal';

/**
 * Регулярный слот режима дня — «Здоровье» в календаре.
 *
 * Хранится ОДНА запись-правило, а не по записи на каждый день: иначе за год
 * накопились бы десятки тысяч строк. Конкретные дни материализуются на лету
 * при отрисовке календаря, а отклонения — в `RoutineOverride`.
 */
export interface Routine extends BaseEntity {
  kind: RoutineKind;
  title: string;
  startMin: MinutesOfDay;
  /** Для сна может превышать остаток суток — значит, переходит через полночь. */
  durationMin: number;
  /**
   * Битовая маска дней недели: бит 0 = понедельник … бит 6 = воскресенье.
   * 127 = каждый день. Позволяет «в выходные завтрак позже».
   */
  weekdaysMask: number;
  /**
   * Обязательный приём пищи (Завтрак / Обед / Ужин) — его нельзя удалить,
   * только сдвинуть. Дополнительные перекусы добавляются кнопкой «+».
   */
  isCore: boolean;
  /** Порядок отображения в настройках профиля. */
  position: number;
  enabled: boolean;
}

/**
 * Отклонение режима в конкретный день: «сегодня обед сдвинулся на час».
 * Правило остаётся нетронутым — меняется только этот день.
 */
export interface RoutineOverride extends BaseEntity {
  routineId: string;
  date: IsoDate;
  startMin: MinutesOfDay | null;
  durationMin: number | null;
  /** Пропуск слота в этот день (например, разгрузочный день без ужина). */
  skipped: boolean;
}

/* ═══════════════════════════ Задачи ═══════════════════════════ */

export type TaskStatus = 'todo' | 'done' | 'cancelled';
/**
 * Две ступени, а не три.
 *
 * «Низкий» отличался от обычного только местом в списке — и человек, выбравший
 * его, не видел ровно ничего. Выбор без последствий хуже отсутствия выбора: он
 * учит не доверять настройкам.
 */
export type TaskPriority = 'normal' | 'high';

export interface Task extends BaseEntity {
  title: string;
  notes: string;
  sphereId: SphereId;
  /** Связь с целью — задача как шаг к результату. */
  goalId: string | null;

  status: TaskStatus;
  priority: TaskPriority;

  /** Оценка длительности в минутах — вход для расчёта бюджета дня. */
  estimateMin: number;

  /**
   * День, на который задача запланирована. `null` — задача в общем списке
   * («когда-нибудь»), не участвует в бюджете конкретного дня.
   */
  plannedDate: IsoDate | null;
  /**
   * Время начала внутри дня. `null` — задача на день есть, но конкретный слот
   * не выбран: она видна в списке дня, но не занимает место в сетке.
   */
  plannedStartMin: MinutesOfDay | null;

  /** Дедлайн — крайний срок. Не путать с `plannedDate` (когда делаю). */
  dueDate: IsoDate | null;
  /** Время дедлайна внутри дня; `null` — «до конца дня». */
  dueMin: MinutesOfDay | null;

  completedAt: number | null;
  /** Сколько раз задача автоматически переносилась. Сигнал «задача зависла». */
  rolloverCount: number;
  /** Дата первого планирования — чтобы видеть, как давно задача откладывается. */
  originalDate: IsoDate | null;

  /** Если задача сгенерирована из привычки — ссылка на неё. */
  habitId: string | null;
  position: number;

  /* ── Повторение ──
   *
   * Повторяющаяся задача — это не бесконечная запись в календаре, а цепочка
   * обычных задач: закрыли текущую — родилась следующая. Так история
   * выполнения сохраняется по-честному (видно, что и когда реально сделано),
   * а незакрытое повторение не размножается само по себе.
   */

  /** `none` — обычная разовая задача. */
  recurrence: RecurrenceType;
  /** Шаг повторения: «раз в 3 дня» → 3. Для `weekdays` не используется. */
  recurrenceInterval: number;
  /** Дни недели для `weekdays`: бит 0 = понедельник. */
  recurrenceWeekdays: number;
  /**
   * От чего отсчитывать следующий раз.
   *
   * `planned` — от запланированной даты: подходит для событий с жёстким
   * ритмом («оплата аренды каждое 5-е число»), пропуск не сдвигает график.
   * `completed` — от даты фактического выполнения: подходит для дел вида
   * «полить цветы раз в 3 дня», где отсчёт идёт от последнего раза.
   */
  recurrenceFrom: RecurrenceAnchor;
  /** Дата, после которой повторения прекращаются. `null` — бессрочно. */
  recurrenceUntil: IsoDate | null;
  /** Общий идентификатор цепочки повторений — связывает экземпляры серии. */
  seriesId: string | null;

  /* ── Связь с календарём устройства ──
   *
   * Задача, поставленная на время в сетке дня, — такое же занятое время, как
   * встреча: коллега, смотрящий общий календарь, должен видеть, что человек
   * занят. Поэтому такие задачи уезжают в календарь устройства и правятся с
   * обеих сторон — теми же тремя полями, что и встречи.
   *
   * Задача без времени наружу не уходит: «когда-нибудь позвонить в банк» — не
   * событие дня, и в чужом календаре ему места нет.
   */

  /** Идентификатор в системном календаре. `null` — задача живёт только здесь. */
  externalId: string | null;
  /** Календарь устройства, в котором она лежит. */
  externalCalendarId: string | null;
  /** Отпечаток состояния на момент последней удачной сверки. */
  syncedHash: string | null;

  /* ── Связь с «Напоминаниями» ──
   *
   * Отдельная от календарной, а не общая. Календарь и напоминания — два
   * разных хранилища с разными идентификаторами, и одно поле на двоих
   * означало бы, что сверка календаря однажды попытается удалить напоминание
   * по его номеру, приняв его за событие.
   */

  /** Идентификатор в «Напоминаниях» устройства. */
  reminderId: string | null;
  /** Список напоминаний, в котором лежит задача. */
  reminderListId: string | null;
  /** Отпечаток напоминания на момент последней удачной сверки. */
  reminderHash: string | null;
}

export type RecurrenceType =
  | 'none'
  | 'daily'
  | 'everyNDays'
  | 'weekdays'
  | 'everyNWeeks'
  | 'monthly'
  /**
   * Последний день каждого месяца. Отдельно от `monthly`: тот держит число
   * месяца, и после 28 февраля уходит на 28 марта — конец месяца теряется.
   */
  | 'lastDayOfMonth';

export type RecurrenceAnchor = 'planned' | 'completed';

/* ═══════════════════════ События календаря ═══════════════════════ */

/**
 * Встреча с фиксированным временем. В отличие от задачи, не переносится
 * автоматически и не закрывается флажком — это внешнее обязательство.
 */
export interface CalendarEvent extends BaseEntity {
  title: string;
  notes: string;
  sphereId: SphereId;
  date: IsoDate;
  startMin: MinutesOfDay;
  durationMin: number;
  location: string;
  /** Минут до начала для напоминания; `null` — без напоминания. */
  remindBeforeMin: number | null;

  /* ── Связь с календарём устройства ──
   *
   * Встреча, пришедшая из системного календаря, остаётся обычной встречей:
   * её можно править и удалять наравне с заведёнными здесь. Эти три поля —
   * всё, что нужно, чтобы правка доехала обратно и не задвоилась.
   */

  /** Идентификатор в системном календаре. `null` — встреча живёт только здесь. */
  externalId: string | null;
  /** Календарь устройства, в котором она лежит. */
  externalCalendarId: string | null;
  /**
   * Отпечаток состояния на момент последней удачной сверки.
   *
   * Android не отдаёт время правки события, поэтому «кто изменился» узнаётся
   * сравнением с этим слепком: сошлось — сторона не менялась, разошлось —
   * менялась. Без него пришлось бы каждый раз гадать, чью версию считать
   * свежей, и правки терялись бы молча.
   */
  syncedHash: string | null;
}

/* ═══════════════════════════ Цели ═══════════════════════════ */

export type GoalStatus = 'active' | 'achieved' | 'paused' | 'archived';

/**
 * Цель по методологии «Атомных привычек».
 *
 * Ключевое поле — `identity`: не «пробежать марафон», а «стать человеком,
 * который бегает». Изменение идентичности удерживает поведение, когда
 * заканчивается мотивация.
 */
export interface Goal extends BaseEntity {
  title: string;
  description: string;
  sphereId: SphereId;
  status: GoalStatus;

  /** «Каким человеком я хочу стать, чтобы добиться этого результата?» */
  identity: string;
  /** «Что нужно делать регулярно, чтобы её достичь» — свободным текстом. */
  regularActions: string;
  /** Признак достижения: как я пойму, что цель выполнена. */
  successCriteria: string;

  targetDate: IsoDate | null;
  /** Ручной прогресс 0–100, если цель не разбита на задачи. */
  manualProgress: number | null;
  achievedAt: number | null;
  position: number;
  color: string | null;
}

/* ═══════════════════════════ Привычки ═══════════════════════════ */

export type HabitType = 'binary' | 'numeric' | 'duration';
export type HabitScheduleType = 'daily' | 'weekdays' | 'timesPerWeek' | 'everyNDays';
export type HabitDirection = 'atLeast' | 'atMost';

export interface Habit extends BaseEntity {
  title: string;
  /** Вопрос для чек-ина: «Ты сегодня медитировал?» — формулировка Loop. */
  question: string;
  notes: string;
  sphereId: SphereId;
  goalId: string | null;

  type: HabitType;
  /** Цель за день. Для numeric хранится ×1000, для duration — в секундах. */
  targetValue: number;
  unit: string;
  /** `atMost` — для вредных привычек: «не больше одной сигареты». */
  direction: HabitDirection;

  scheduleType: HabitScheduleType;
  weekdaysMask: number;
  /** «3 раза за 7 дней» → numerator = 3, denominator = 7. */
  freqNumerator: number;
  freqDenominator: number;
  intervalDays: number;

  /** Напоминание: время внутри дня; `null` — без напоминания. */
  reminderMin: MinutesOfDay | null;

  /* Поля методологии «Атомных привычек» */
  identity: string;
  cue: string;
  reward: string;
  /** «Я буду [привычка] в [время] в [месте]» — implementation intention. */
  intentionTime: string;
  intentionPlace: string;
  /** Версия привычки на две минуты — правило лёгкого старта. */
  twoMinuteVersion: string;
  /** Привязка к уже существующей привычке (habit stacking). */
  stackAfterHabitId: string | null;

  startDate: IsoDate;

  /**
   * Сколько дней привычку нужно выполнить, чтобы считать её освоенной.
   *
   * По умолчанию 21 — самый известный ориентир, хотя и спорный: исследование
   * Лалли показало разброс от 18 до 254 дней, а среднее ближе к 66. Поэтому
   * срок настраивается: «пить воду» закрепляется быстрее, чем «бегать по
   * утрам», и одна цифра на все привычки была бы неправдой.
   *
   * Считаются выполненные дни, а не подряд идущие: пропуск сбивает серию, но
   * не отменяет уже сделанного. Иначе один больничный обнулял бы три недели
   * работы, а это отбивает желание продолжать вернее всего.
   */
  targetDays: number;

  archivedAt: number | null;
  color: string | null;
  icon: string;
  position: number;
}

/** Срок закрепления привычки по умолчанию. */
export const DEFAULT_HABIT_TARGET_DAYS = 21;

/** Значения-сентинелы совместимы с Loop Habit Tracker. */
export const HABIT_VALUE = {
  UNKNOWN: -1,
  NO: 0,
  YES_AUTO: 1,
  YES_MANUAL: 2,
  SKIP: 3,
} as const;

export interface HabitEntry extends BaseEntity {
  habitId: string;
  date: IsoDate;
  /** Сентинел из `HABIT_VALUE` либо фактическое значение (numeric ×1000). */
  value: number;
  note: string;
}

/* ═══════════════════════════ Здоровье ═══════════════════════════ */

/**
 * Замер за день. Все показатели опциональны: пользователь может внести
 * только вес или только давление, график строится по имеющимся точкам.
 */
export interface HealthRecord extends BaseEntity {
  date: IsoDate;
  weightKg: number | null;
  /** Рост берётся из профиля, но может быть уточнён на конкретную дату. */
  heightCm: number | null;
  systolic: number | null;
  diastolic: number | null;
  pulse: number | null;
  temperature: number | null;
  /** Часы сна — при желании заполняются вручную. */
  sleepHours: number | null;
  steps: number | null;
  mood: number | null;
  note: string;
}

/* ═══════════════════════════ Таблетница ═══════════════════════════ */

export type MedicationForm =
  | 'tablet'
  | 'capsule'
  | 'syrup'
  | 'suspension'
  | 'solution'
  | 'injection'
  | 'drops'
  | 'spray'
  | 'ointment'
  | 'gel'
  | 'cream'
  | 'patch'
  | 'suppository'
  | 'powder'
  | 'inhaler';

export type DoseUnit = 'pcs' | 'mg' | 'g' | 'ml' | 'drop' | 'iu' | 'puff' | 'sachet';

/** Связь с приёмом пищи. Соответствует `Timing.when` из стандарта FHIR. */
export type MealRelation =
  'any' | 'beforeMeal' | 'withMeal' | 'afterMeal' | 'emptyStomach' | 'bedtime';

/**
 * Что происходит с препаратом.
 *
 * `none` — ничего: лекарство просто лежит в аптечке. Так в приложение попадает
 * всё, что куплено «на всякий случай»: жаропонижающее, капли от насморка,
 * перекись. Приёмов у такой записи нет и быть не может — её дело помнить срок
 * годности и остаток.
 */
export type CourseType = 'daysCount' | 'untilDate' | 'indefinite' | 'asNeeded' | 'none';
export type MedScheduleType = 'timesPerDay' | 'everyNHours' | 'weekdays' | 'everyNDays' | 'cycle';

export interface Medication extends BaseEntity {
  name: string;
  /** МНН — действующее вещество. Помогает не пропустить дубль препарата. */
  activeIngredient: string;
  form: MedicationForm;
  doseAmount: number;
  doseUnit: DoseUnit;
  /** Дозировка с упаковки, например «500 мг». */
  strength: string;

  courseType: CourseType;
  /**
   * Приёмы пищи, к которым привязан курс, — идентификаторы правил режима дня.
   *
   * Пусто — время приёма задано часами (`doseTimes`). Иначе время считается на
   * каждый день из режима этого дня: сдвинутый на один день обед двигает только
   * свой приём, а правка правила — все будущие. Раньше привязка записывала
   * время снимком, и разовый сдвиг уезжал в постоянное расписание.
   */
  mealRoutineIds: string[];
  startDate: IsoDate;
  endDate: IsoDate | null;
  durationDays: number | null;

  mealRelation: MealRelation;
  /** «За 30 минут до еды» — направление задаёт `mealRelation`. */
  mealOffsetMin: number;

  scheduleType: MedScheduleType;
  timesPerDay: number;
  intervalHours: number;
  weekdaysMask: number;
  intervalDays: number;
  /** Циклы вида 21/7 — контрацептивы, курсовая терапия. */
  cycleActiveDays: number;
  cyclePauseDays: number;

  /** Явные времена приёма в минутах от полуночи. */
  doseTimes: MinutesOfDay[];

  /* Учёт остатка упаковки */
  stockQuantity: number | null;
  packageSize: number | null;
  /** Порог для напоминания «пора купить». */
  refillThreshold: number;

  /**
   * Из чего состоит курс: ссылки на карточки лекарств, которые в него входят.
   *
   * Пусто — обычное лекарство: одна упаковка, один курс. Непусто — курс, для
   * которого лекарства смешивают: две мази в одном нанесении, две капли подряд.
   * Такой курс — это применение, а не упаковка: срок годности и остаток есть у
   * составляющих, и в аптечке лежат они, а не он.
   */
  componentIds: string[];

  /* ── Аптечка: что лежит дома ── */

  /**
   * Срок годности с упаковки. `null` — не указан.
   *
   * На упаковке обычно стоят месяц и год; днём берётся последний день месяца —
   * так, как это понимает и производитель.
   */
  expiryDate: IsoDate | null;
  /** Когда упаковку вскрыли. `null` — ещё запечатана или дата неизвестна. */
  openedAt: IsoDate | null;
  /**
   * Сколько дней препарат годен после вскрытия — «использовать в течение 28
   * дней» с упаковки. Считается от `openedAt` и может закончиться раньше
   * срока годности: вскрытый флакон живёт своей жизнью.
   */
  openedShelfDays: number | null;
  /** Для чего лекарство: ключ из справочника назначений. `null` — не указано. */
  purposeKey: string | null;
  /** Где лежит: «аптечка», «холодильник», «в машине». Пусто — не указано. */
  storagePlace: string;

  notes: string;
  prescribedBy: string;
  color: string | null;
  isActive: boolean;
  archivedAt: number | null;
}

export type DoseStatus = 'pending' | 'taken' | 'skipped' | 'postponed' | 'missed';
export type SkipReason = 'forgot' | 'sideEffect' | 'ranOut' | 'feltBetter' | 'other';

/**
 * Одна запланированная доза. Материализуется планировщиком на горизонт
 * вперёд, чтобы список «на сегодня» не пересчитывал правила при каждом рендере
 * и чтобы можно было завести локальное уведомление на точное время.
 */
export interface DoseEvent extends BaseEntity {
  medicationId: string;
  date: IsoDate;
  /**
   * Приём пищи, из которого посчитано время. `null` — время задано часами.
   *
   * Без этой ссылки приём узнавался по времени, а у привязанного к еде курса
   * время меняется вместе с завтраком. Стоило сдвинуть еду в календаре после
   * того, как таблетка уже отмечена принятой, — и на новом времени заводился
   * второй приём: старый остался в журнале, нового «не хватало». Теперь связь
   * названа явно, и сдвиг еды двигает свой приём, а не порождает лишний.
   */
  mealRoutineId: string | null;
  scheduledMin: MinutesOfDay;
  plannedAmount: number;
  status: DoseStatus;
  takenAt: number | null;
  actualAmount: number | null;
  snoozedUntil: number | null;
  skipReason: SkipReason | null;
  note: string;
}

/* ═══════════════════════════ Покупки ═══════════════════════════ */

export type ShoppingUnit = 'pcs' | 'kg' | 'g' | 'l' | 'ml' | 'pack' | 'bottle' | 'can' | 'box';

export interface ShoppingList extends BaseEntity {
  name: string;
  /** Дата похода в магазин — основа фильтра «сегодня / будущее». */
  plannedDate: IsoDate | null;
  color: string | null;
  archivedAt: number | null;
  position: number;
}

export interface ShoppingItem extends BaseEntity {
  listId: string;
  name: string;
  /** Ключ категории из справочника `shopping-catalog.ts`. */
  categoryKey: string;
  quantity: number | null;
  unit: ShoppingUnit;
  note: string;
  /** Где купить: «Пятёрочка», «рынок». Пусто — не указан. */
  store: string;
  price: number | null;
  isChecked: boolean;
  checkedAt: number | null;
  position: number;
}

/**
 * Обучение автокатегоризации: если пользователь перенёс «авокадо» из «Прочего»
 * в «Овощи и фрукты», следующий раз оно попадёт туда сразу.
 */
export interface ProductPreference extends BaseEntity {
  /** Нормализованное имя товара — ключ поиска. */
  productKey: string;
  categoryKey: string;
  defaultUnit: ShoppingUnit;
  defaultQuantity: number | null;
  /** Где этот товар покупали в последний раз. Пусто — не указан. */
  store: string;
  usageCount: number;
  lastUsedAt: number;
  isFavorite: boolean;
}

/* ═══════════════════════ Книги и фильмы ═══════════════════════ */

export type MediaKind = 'book' | 'movie';
/** Формат фильма — см. `shared/config/media-formats`. У книг — `null`. */
export type MediaFormat =
  | 'film'
  | 'series'
  /** Азиатский сериал: японский, корейский, китайский, тайваньский. */
  | 'dorama'
  | 'cartoon'
  | 'animatedSeries'
  | 'anime'
  | 'documentary'
  | 'show';
export type MediaStatus = 'planned' | 'inProgress' | 'done' | 'dropped';

export interface MediaItem extends BaseEntity {
  kind: MediaKind;
  title: string;
  originalTitle: string;
  /** Автор книги или режиссёр фильма. */
  author: string;
  year: number | null;
  /** Жанры, подтянутые из Open Library / TMDB. */
  genres: string[];
  /** Формат фильма: фильм, сериал, мультфильм… У книг — `null`. */
  format: MediaFormat | null;
  /**
   * Любимое — отдельно от оценки: «10 из 10» ставят и тому, к чему больше не
   * вернёшься, а любимое — то, что перечитывают и пересматривают.
   */
  favorite: boolean;
  description: string;
  /**
   * Статья Википедии, из которой взято описание, — ссылка под ним: и источник,
   * как требует лицензия, и где прочитать дальше. `null` — описание из
   * каталога или его нет.
   */
  descriptionUrl: string | null;
  coverUrl: string | null;
  /** Идентификатор во внешнем каталоге — чтобы не задваивать записи. */
  externalId: string | null;
  externalSource: 'openlibrary' | 'tmdb' | 'wikidata' | 'manual';

  status: MediaStatus;
  /** Оценка 1–10; `null` — не оценено. */
  rating: number | null;
  review: string;
  finishedAt: IsoDate | null;
  position: number;
}

/* ═══════════════════ Очередь синхронизации ═══════════════════ */

export type OutboxOperation = 'create' | 'update' | 'delete';

/**
 * Журнал локальных изменений (паттерн Outbox).
 *
 * Приложение всегда пишет в локальную базу и добавляет запись сюда. Когда
 * появится сервер, `SyncEngine` разберёт очередь по порядку. Благодаря этому
 * интерфейс работает мгновенно и полностью офлайн, а сеть становится
 * необязательной деталью, а не условием работы.
 */
export interface OutboxRecord {
  id: string;
  userId: string;
  entity: string;
  entityId: string;
  operation: OutboxOperation;
  payload: unknown;
  createdAt: number;
  /**
   * Версия записи, на которой построено изменение.
   *
   * Без неё сервер не может отличить повтор доставки от нового изменения и не
   * может понять, поверх чего наложен патч: приложение обещало идемпотентность,
   * а отправляло голый набор полей. При слиянии нескольких правок берётся
   * самая ранняя — состояние, от которого человек отталкивался.
   */
  baseRevision: number;
  /** Версия записи после изменения — по ней сервер узнаёт повторную доставку. */
  revision: number;
  /** Число неудачных попыток отправки — для экспоненциальной паузы. */
  attempts: number;
  /**
   * Когда попытка была последней.
   *
   * Пауза считается от неё, а не от создания записи: старое изменение иначе
   * всегда «готово к повтору», и защита от шторма не работает именно тогда,
   * когда нужна — при долгом отказе сервера.
   */
  lastAttemptAt: number | null;
  lastError: string | null;
}

/* ═══════════════════════════ Прочее ═══════════════════════════ */

/**
 * Снимок данных на день — автоматическая резервная копия.
 *
 * Не сущность в общем смысле: снимок никуда не синхронизируется, не имеет
 * ревизии и не попадает ни в выгрузку, ни в счётчик записей. Копия, лежащая
 * внутри копии, — бессмыслица, растущая в геометрической прогрессии.
 *
 * Снимок хранится в браузере, где записать файл на диск без спроса нельзя.
 * Это защита от беды внутри приложения — ошибочной замены всех данных,
 * массового удаления, сбоя, — но не от потери устройства: вместе с данными
 * сайта исчезнет и он. Поэтому карточка копии продолжает просить файл.
 *
 * В приложении на телефоне копия пишется настоящим файлом, и `payload` тогда
 * не нужен: хранить те же мегабайты второй раз незачем.
 */
export interface BackupSnapshot {
  id: string;
  userId: string;
  /** День, за который снят снимок: по нему видно, что сегодня копия уже есть. */
  date: IsoDate;
  createdAt: number;
  /** Сколько записей внутри — это число видит человек в карточке. */
  records: number;
  /** Путь к файлу на устройстве, если копия легла файлом. */
  path: string | null;
  /**
   * Сам снимок — сжатый JSON. Только для копии внутри приложения.
   *
   * Хранится байтами, а не строкой: копий до двадцати двух, каждая — полная
   * выгрузка, и в несжатом виде они вытеснили бы из хранилища сами данные.
   */
  payload: Uint8Array | null;
}

/** Ключ-значение для локальных настроек, не требующих синхронизации. */
export interface AppSetting {
  key: string;
  value: unknown;
  updatedAt: number;
}

/**
 * Серия закрытых дней подряд — механика вовлечения в духе Duolingo.
 * Хранится агрегатом, чтобы не пересчитывать по всей истории задач.
 */
export interface StreakState {
  id: string;
  userId: string;
  currentStreak: number;
  bestStreak: number;
  lastCompletedDate: IsoDate | null;
  totalCompleted: number;
  updatedAt: number;
}
